import React from 'react';
import { action, observable } from 'mobx';
import api from 'core/util/api';
import { camelCase, isEmpty, mapKeys, startCase } from 'lodash';
import { showErrorToast, showSuccessToast } from 'core/components';
import ModelVisibilityLabel, { MODEL_VISIBILITY_TAGS } from 'app/components/ModelVisibilityLabel';
import UserQuestionCollection from './UserQuestionCollection';

const PROVIDER_ATTR = 'providers';
const SUPPORT_CREDENTIALS_ATTR = 'support_credentials';
const DEFAULT_PROVIDER_ATTR = 'default';
const DEPRECATED_VISIBILITY = 'deprecated';

class AiStore {
  @observable.ref
  allAllowedModels = {};

  @observable
  loading = true;

  userQuestionCollection = new UserQuestionCollection();

  @action
  updateUserQuestionCollection = ({ startTime, endTime } = {}) => {
    const sessions = this.store?.$kaizen?.chatSessionCollection?.models ?? [];

    const cutoff = startTime ? new Date(startTime * 1000) : new Date(0);
    const end = endTime ? new Date(endTime * 1000) : new Date();

    const userCounts = {};
    sessions
      .filter((s) => {
        const d = new Date(s.get('created_at'));
        return d >= cutoff && d <= end;
      })
      .filter((s) => !s.isAutomatedChat)
      .forEach((s) => {
        const email = s.get('user')?.user_email || 'Unknown';
        userCounts[email] = (userCounts[email] || 0) + 1;
      });

    const rows = Object.entries(userCounts)
      .map(([user_email, count]) => ({ user_email, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);

    this.userQuestionCollection.populate(rows);
    this.userQuestionCollection.hasFetched = true;
  };

  @action
  initialize() {
    const { $app } = this.store;
    if (!$app.isSubtenant) {
      this.loadModels();
    }
  }

  @action
  loadModels = async () => {
    const hasPermission = this.store.$auth.hasPermission('aiModels', { overrideForSudo: false });
    if (hasPermission) {
      if (!isEmpty(this.allAllowedModels)) {
        this.loading = false;
      } else {
        // the node server decides which models a user can see based on their sudo status
        api
          .get('/api/ui/ai/models', { showErrorToast: false })
          .then(
            action((models) => {
              this.allAllowedModels = models;
              this.loading = false;
            })
          )
          .catch((error) => {
            console.error('Failed to load AI models:', error?.message || 'Unknown error');
            this.loading = false;
          });
      }
    } else {
      this.loading = false;
    }
  };

  async testCredential(data) {
    return api
      .post('/api/ui/ai/test/credentials', { data, showErrorToast: false })
      .then(
        action(() => {
          showSuccessToast('Credentials authenticated successfully.');
        })
      )
      .catch(
        action((err) => {
          showErrorToast((err && err.message) || 'Authentication failed. Try using a different token.');
        })
      );
  }

  isDefaultProvider = (provider) => provider === DEFAULT_PROVIDER_ATTR;

  getAllowedModels = (tier) => (tier ? this.allAllowedModels[tier] || {} : this.allAllowedModels);

  getProviderInfo = (tier, provider) => {
    const allowedModels = this.getAllowedModels(tier);

    if (isEmpty(allowedModels)) {
      return undefined;
    }

    return this.isDefaultProvider(provider)
      ? allowedModels[provider]
      : allowedModels[PROVIDER_ATTR]?.find(({ id }) => id === provider);
  };

  defaultProviderName = (tier) =>
    // note: assuming provider_id is in set of existing providers for tier
    this.getProviderInfo(tier, this.getAllowedModels(tier)[DEFAULT_PROVIDER_ATTR]?.provider_id)?.name;

  isCredentialSupported = (tier, provider) => {
    const providerInfo = this.getProviderInfo(tier, provider);
    return this.isDefaultProvider(provider) || (providerInfo && providerInfo[SUPPORT_CREDENTIALS_ATTR]);
  };

  get tiers() {
    return Object.keys(this.allAllowedModels).map((tier) => ({ ...this.allAllowedModels[tier] }));
  }

  getProviderOptions = (tier) => {
    const allowedModels = this.getAllowedModels(tier);
    const options = [{ value: DEFAULT_PROVIDER_ATTR, label: 'Kentik default' }];
    if (allowedModels) {
      Object.values(allowedModels[PROVIDER_ATTR] || {}).forEach(({ id, name }) => {
        options.push({ value: id, label: name });
      });
    }

    return options;
  };

  getProviderModels = (tier, provider) => {
    const providerInfo = this.getProviderInfo(tier, provider);
    if (!providerInfo) {
      return [];
    }
    return (this.isDefaultProvider(provider) ? [providerInfo] : providerInfo.models) || [];
  };

  getModelLabel = ({ name, visibility }) =>
    MODEL_VISIBILITY_TAGS[visibility] ? <ModelVisibilityLabel name={name} visibility={visibility} /> : name;

  /**
   * Customers can't pick deprecated models. If `savedModel` (the company's saved model for this provider) has been
   * deprecated, it is returned as a hidden option: the select shows it with its tag and the field still validates,
   * but it isn't offered in the menu.
   */
  getModelOptions = (tier, provider, savedModel) => {
    const isSudoer = this.store?.$auth?.isSudoer;

    return this.getProviderModels(tier, provider)
      .filter(({ id, visibility }) => isSudoer || visibility !== DEPRECATED_VISIBILITY || id === savedModel)
      .map((model) => {
        const label = this.getModelLabel(model);
        const option =
          typeof label === 'string' ? { label, value: model.id } : { label, filterLabel: model.name, value: model.id };
        return !isSudoer && model.visibility === DEPRECATED_VISIBILITY ? { ...option, hidden: true } : option;
      });
  };

  getRegionOptions = (tier, provider, model) => {
    const providerInfo = this.getProviderInfo(tier, provider);
    const options = [];
    if (providerInfo) {
      const { models } = providerInfo;
      (models?.find(({ id }) => id === model)?.regions || []).forEach((region) => {
        options.push({ label: region, value: region });
      });
    }
    return options;
  };

  getTierSettingsKey = (tier) => `${camelCase(tier)}Model`;

  getTierLabel = (tier) => startCase(tier);

  getFieldsByTier = (tier) => {
    const label = this.getTierLabel(tier);
    const baseFields = {
      provider: {
        label: 'Provider',
        defaultValue: DEFAULT_PROVIDER_ATTR,
        options: this.getProviderOptions(tier)
      },
      region: {
        label: 'Region',
        messages: { required: `${label} Model: Please select a region.` }
      },
      model: {
        label: 'Model',
        rules: 'required',
        messages: { required: `${label} Model: Please select a model.` }
      },
      credential: {
        label: 'API Token',
        messages: { required: `${label} Model: Please select an API Token.` }
      },
      keyTypes: {
        defaultValue: ''
      }
    };

    return mapKeys(baseFields, (value, key) => `${this.getTierSettingsKey(tier)}.${key}`);
  };

  get fields() {
    const fields = {
      companyPrompt: { label: 'Custom Network Context', defaultValue: '', rules: 'max:100000' },
      deviceConfigurationToolEnabled: {
        helpText:
          'Allow AI Advisor to access device configurations and their history of changes. Configs and diffs in Device Details do not require AI Advisor.',
        defaultValue: true
      },
      deviceCommandToolEnabled: {
        label: 'Device Command Tool',
        helpText: 'Empower AI Advisor to securely run read-only "show" commands for immediate diagnostic context.',
        defaultValue: false
      },

      'deviceCommandAcls.aclMode': {
        label: 'ACL Mode',
        defaultValue: 'allow_all'
      },
      'deviceCommandAcls.rules': {
        label: 'Rules',
        defaultValue: [],
        isComplexArray: true
      },
      'deviceCommandAcls.rules[].matchType': {
        label: 'Match Type',
        rules: 'required',
        defaultValue: 'exact'
      },
      'deviceCommandAcls.rules[].pattern': {
        label: 'Pattern',
        rules: 'required',
        defaultValue: ''
      }
    };
    this.tiers.map(({ name }) => Object.assign(fields, this.getFieldsByTier(name)));
    return fields;
  }
}

export default new AiStore();
